/**
 * AR Vision Demo — per-customer comms-notes guidance seed.
 *
 * THROWAWAY DEMO CODE. Populates `commsNotesByCustomer`: the curated free-text
 * steer the real `CustomerCommsNotesCard` shows on each customer profile (and
 * which the AR LLM prompts consume). The guidance mirrors what the Email
 * Intelligence demo "learned" about how to communicate with each customer —
 * tone, who actually releases payment, recurring disputes, response cadence —
 * so the comms-notes card reads as the distilled output of that corpus work.
 * Granite Peak / Cascade / Crestline draw directly on the email-intel comms
 * profiles, learned facts, and recommendations in
 * `email-intel/email-intel-seed.ts`.
 *
 * Keyed by the vision/technical customer ids the profile page navigates to
 * (NOT the email-intel `intel-*` cast ids). Delete with frontend/src/demo/ar.
 */

import type { ArCustomerCommsNotes } from '@erp/rpc';

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

/** Build one comms-notes record (notes curated by the agent — no human editor). */
function note(text: string, daysAgo: number): ArCustomerCommsNotes {
  return {
    notes: text.trim(),
    updatedAt: isoDaysAgo(daysAgo),
    updatedByUserId: null,
  };
}

// Customer ids — must match fixture-seed.ts `ID` and the technical seed.
const GRANITE = 'demo-cust-granite-0003';
const CASCADE = 'demo-cust-cascade-0002';
const CRESTLINE = 'tech-cust-crestline-0001';
const PILLAR = 'demo-cust-pillar-0001';
const CEDAR = 'demo-cust-cedar-0004';

/** Per-customer curated comms guidance, derived from the email-intel learnings. */
export function buildCommsNotesSeed(): Record<string, ArCustomerCommsNotes> {
  return {
    // Granite Peak — combative freight-disputer (richest email-intel profile:
    // best-contact, recurring-dispute, latency, promise-to-pay facts).
    [GRANITE]: note(
      `Tone: warm but firm — open with a little rapport, then a direct ask. This is a combative account, so keep the pressure low and the evidence up front.

Who to contact: CC Maria Okafor (AP Manager) directly — m.okafor@granitepeak-dist.com. The generic AP inbox auto-replies and isn't monitored daily; Maria is the one who actually approves and releases checks, so addressing her directly cuts the reply cycle roughly in half.

Recurring issue — freight: short-pays the freight line on ~3 of every 4 invoices. Pre-empt it by attaching the signed POD up front. Every time we've led with the POD, they've released the balance within a week.

Cadence: typically replies in 3–5 business days, and usually only after a second nudge — don't read silence as refusal.`,
      3,
    ),

    // Cascade — loyal, deferential 10-year customer; soft tone wins.
    [CASCADE]: note(
      `Tone: soft and relationship-first. Loyal 10-year customer who is deferential and apologetic when they run late, so a gentle, low-pressure nudge lands far better than a firm demand.

Who to contact: David Cho, Controller — david.cho@cascade-mfg.com.

Approach: reference the long relationship and assume good faith ("I know it's just a timing thing"). Give them room — "whenever it fits your cycle." They respond well and don't need to be chased hard.

Cadence: usually replies within 2–3 business days.`,
      6,
    ),

    // Crestline — responsive, collaborative; brief and friendly is enough.
    [CRESTLINE]: note(
      `Tone: friendly and brief — a quick, warm heads-up is all that's needed. Crestline is responsive and easy to work with.

Who to contact: Maria Alvarez, AP Specialist — maria.alvarez@crestlinemarkets.com. Turns replies around in about a business day.

Approach: keep it short and light, no deadlines. They pay once the paperwork is clear, so make sure any backup (remittance, POD) is attached or easy to resend.

Cadence: ~1 business day turnaround.`,
      4,
    ),

    // Harbor Foods — reliable flagship that pays on time. (Pillar, Inc is OUR
    // company / the ERP org everywhere, so the flagship customer is Harbor Foods.)
    [PILLAR]: note(
      `Tone: professional and concise. Reliable, high-volume account that pays on time (~93% on-time, ~27 days to pay), so routine reminders are rarely needed.

Who to contact: Marcus Delgado, Accounts Payable — ap@harborfoods.com.

Approach: straightforward and businesslike — lead with the invoice and PO reference. No need to soften; they keep their account current and prefer efficiency.`,
      12,
    ),

    // Cedar Ridge — reporting-beat customer on a declining trend after an AP
    // handoff; guidance matches that storyline.
    [CEDAR]: note(
      `Tone: attentive and proactive. Payments have been trending later since a new AP contact took over — worth staying on top of this account before it slips further.

Approach: keep reminders timely and clear, and confirm you have the right billing contact (the AP handoff has caused some delays). A short, friendly check-in to re-establish the relationship with the new contact helps.

Watch: declining payment trend — flag it if it continues.`,
      2,
    ),
  };
}
